import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import { HW_TINT_BLOCK, type TintTone } from "./theme";
import type { InsightsEnvelope, AnalyticsViewProps } from "./types";

const TONE: Record<string, TintTone> = {
  action: "danger",
  watch: "warning",
  info: "neutral",
};

export function InsightsPanelView({
  data,
  loading,
  error,
}: AnalyticsViewProps<InsightsEnvelope>) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const items = (data?.insights ?? []).filter((i) => !dismissed.has(i.id));

  return (
    <Panel title={t("homework.analytics.insights.title")}>
      {loading ? (
        <LoadingRows rows={2} />
      ) : error || !data ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !items.length ? (
        <EmptyState message={t("homework.analytics.insights.none")} />
      ) : (
        <ul className="space-y-2 p-4">
          {items.map((i) => (
            <li
              key={i.id}
              className={`rounded-lg p-3 ${HW_TINT_BLOCK[TONE[i.severity] ?? "neutral"]}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[13px] font-semibold">{i.title}</p>
                  <p className="text-[12px]">{i.detail}</p>
                  <p className="mt-1 text-[12px] opacity-90">💡 {i.recommendation}</p>
                </div>
                <button
                  type="button"
                  className="text-[11px] underline opacity-70"
                  aria-label={`${t("homework.analytics.insights.dismiss")}: ${i.title}`}
                  onClick={() => setDismissed((s) => new Set(s).add(i.id))}
                >
                  {t("homework.analytics.insights.dismiss")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
